import {type FC} from 'react';
import type {PlaylistInfo} from '../types';
import {getCurrentFocusKey, setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import {DownloadBtn, ProgressBarFill, ThumbnailCard, ThumbnailImage, VideoTitle} from '../types/Replay.styled.ts';
import useReplayStore from '../../../store/useReplayStore.ts';
import useYTdownloadStore from '../../../store/useYTdownloadStore.ts';

interface ThumbnailCardItemProps {
    videoInfo: PlaylistInfo;
    sectionIndex: number;
    itemIndex: number;
}

const ThumbnailCardItem: FC<ThumbnailCardItemProps> = ({videoInfo, sectionIndex, itemIndex}) => {
    const {setLastFocus, handleClick} = useReplayStore();
    const {downloadProgress, startDownload} = useYTdownloadStore();

    const {ref: cardSectionRef, focused: cardSectionFocused} = useFocusable({
        focusKey: `thumbnail-${sectionIndex}-${itemIndex}-${videoInfo.id}`,
        onFocus: () => {
            if (cardSectionRef.current) {
                cardSectionRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        },
        onEnterPress: () => {
            handleClick(videoInfo);
            setLastFocus(`thumbnail-${sectionIndex}-${itemIndex}-${videoInfo.id}`);
        },
        onArrowPress: (direction) => {
            if (direction === 'up' && getCurrentFocusKey().startsWith('thumbnail-0')) {
                setFocus('go-download-page');
                return false;
            }
            return true;
        },
    });

    const {ref: downloadSectionRef, focused: downloadSectionFocused} = useFocusable({
        focusKey: `thumbnail-${sectionIndex}-${itemIndex}-${videoInfo.id}-download`,
        onFocus: () => {
            if (downloadSectionRef.current) {
                downloadSectionRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        },
        onEnterPress: () => {
            setLastFocus(`thumbnail-${sectionIndex}-${itemIndex}-${videoInfo.id}`);
            startDownload(videoInfo);
        },
    });

    const progress = downloadProgress[videoInfo.id];
    const isDownloading = progress !== undefined && progress < 100;

    return (
        <>
            <ThumbnailCard ref={cardSectionRef} $focused={cardSectionFocused} tabIndex={-1}>
                <ThumbnailImage src={videoInfo.snippet.thumbnails.high?.url} alt={videoInfo.snippet.title} />
                <VideoTitle>{videoInfo.snippet.title}</VideoTitle>
            </ThumbnailCard>
            <DownloadBtn
                ref={downloadSectionRef}
                $focused={downloadSectionFocused}
                onClick={() => handleClick(videoInfo)}
            >
                {isDownloading ? `다운로드 중... ${progress}%` : '다운로드'}
                {progress !== undefined && <ProgressBarFill $progress={progress} />}
            </DownloadBtn>
        </>
    );
};

export default ThumbnailCardItem;
